import { motion } from "motion/react";
import type { ReactNode } from "react";
import { useRevealReducedMotion } from "@/lib/reveal";

// Shared scroll-reveal primitives for the landing. Ported from the Radiant
// reference so every section animates with one consistent easing/curve instead
// of each component hand-rolling its own variants. All three no-op under
// `prefers-reduced-motion`.

type Direction = "up" | "down" | "left" | "right";

const OFFSET: Record<Direction, { x: number; y: number }> = {
  up: { x: 0, y: 30 },
  down: { x: 0, y: -30 },
  left: { x: 30, y: 0 },
  right: { x: -30, y: 0 },
};

// easeOutQuint-style curve — fast start, long settle. Matches the .glass-card
// hover transition in index.css so scroll reveals and hovers share one feel.
const EASE = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: "-100px" } as const;

export function AnimatedContainer({
  children,
  className,
  delay = 0,
  direction = "up",
  staggerChildren = false,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  direction?: Direction;
  staggerChildren?: boolean;
}) {
  const reduce = useRevealReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT}
      variants={{
        hidden: { opacity: 0, filter: "blur(8px)", ...OFFSET[direction] },
        visible: {
          opacity: 1,
          x: 0,
          y: 0,
          filter: "blur(0px)",
          transition: {
            duration: 0.6,
            delay,
            ease: EASE,
            ...(staggerChildren && { staggerChildren: 0.1, delayChildren: delay }),
          },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

/** A single revealed item — pair with a staggering parent, or use standalone. */
export function AnimatedItem({
  children,
  className,
  delay = 0,
  direction = "up",
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  direction?: Direction;
}) {
  const reduce = useRevealReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT}
      variants={{
        hidden: { opacity: 0, filter: "blur(8px)", ...OFFSET[direction] },
        visible: { opacity: 1, x: 0, y: 0, filter: "blur(0px)" },
      }}
      transition={{ duration: 0.5, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function StaggeredList({
  children,
  className,
  itemClassName,
  staggerDelay = 0.1,
  direction = "up",
}: {
  children: ReactNode[];
  className?: string;
  itemClassName?: string;
  staggerDelay?: number;
  direction?: Direction;
}) {
  const reduce = useRevealReducedMotion();
  if (reduce) {
    return (
      <div className={className}>
        {children.map((child, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static, order-stable list
          <div key={i} className={itemClassName}>
            {child}
          </div>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={VIEWPORT}
      variants={{
        hidden: { opacity: 1 },
        visible: { opacity: 1, transition: { staggerChildren: staggerDelay, delayChildren: 0.1 } },
      }}
    >
      {children.map((child, i) => (
        <motion.div
          // biome-ignore lint/suspicious/noArrayIndexKey: static, order-stable list
          key={i}
          className={itemClassName}
          variants={{
            hidden: { opacity: 0, filter: "blur(8px)", ...OFFSET[direction] },
            visible: {
              opacity: 1,
              x: 0,
              y: 0,
              filter: "blur(0px)",
              transition: { duration: 0.6, ease: EASE },
            },
          }}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
}
